import { Trash2, Edit2, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useShopping } from '../contexts/ShoppingContext';
import { useGuestMode } from '../hooks/useGuestMode';
import { QuantitySelector, Modal } from './ui';
import { CalendarCustomizer } from './CalendarCustomizer';
import { BusinessCardCustomizer } from './BusinessCardCustomizer';
import { CalendarCustomization, BusinessCardCustomization, CartItem } from '../types';
import { useState } from 'react';

const authCheckoutEnabled = import.meta.env.VITE_AUTH_CHECKOUT_ENABLED === 'true';

export function CartPage() {
  const navigate = useNavigate();
  const { state, dispatch } = useShopping();
  const { isGuest } = useGuestMode();
  const { cart } = state;
  const [editingCartItem, setEditingCartItem] = useState<CartItem | null>(null);
  const [editingCustomization, setEditingCustomization] = useState<CalendarCustomization | null>(null);
  const [editingBusinessCardCustomization, setEditingBusinessCardCustomization] = useState<BusinessCardCustomization | null>(null);

  const getDiscountTier = (item: CartItem) =>
    item.product.quantityDiscounts
      ?.filter(t => item.quantity >= t.minQuantity && (t.maxQuantity === null || item.quantity <= t.maxQuantity))
      .sort((a, b) => b.minQuantity - a.minQuantity)[0];

  const getDiscountedUnitPrice = (item: CartItem) => {
    const tier = getDiscountTier(item);
    return tier ? tier.customPrice : item.variation.price;
  };

  const getPersonalizationFeePerUnit = (item: CartItem) =>
    item.personalizations?.reduce((sum, p) => sum + (p.fee ?? 0), 0) ?? 0;

  const originalTotal = cart.reduce((total, item) => total + (item.variation.price + getPersonalizationFeePerUnit(item)) * item.quantity, 0);
  const totalPrice = cart.reduce((total, item) => total + (getDiscountedUnitPrice(item) + getPersonalizationFeePerUnit(item)) * item.quantity, 0);
  const totalSavings = originalTotal - totalPrice;

  const handleUpdateQuantity = (cartItemId: string, newQuantity: number) => {
    dispatch({
      type: 'UPDATE_QUANTITY',
      payload: { cartItemId, quantity: newQuantity }
    });
  };

  const handleRemoveItem = (cartItemId: string) => {
    dispatch({
      type: 'REMOVE_FROM_CART',
      payload: cartItemId
    });
  };

  const handleEditCustomization = (item: CartItem) => {
    setEditingCartItem(item);
    setEditingCustomization(item.calendarCustomization || null);
  };

  const handleEditBusinessCardCustomization = (item: CartItem) => {
    setEditingCartItem(item);
    setEditingBusinessCardCustomization(item.businessCardCustomization || null);
  };

  const handleSaveCustomization = () => {
    if (editingCartItem && editingCustomization) {
      const cartItemId = editingCartItem.id || `${editingCartItem.productId}-${editingCartItem.variationId}`;
      dispatch({ type: 'UPDATE_CALENDAR_CUSTOMIZATION', payload: { cartItemId, calendarCustomization: editingCustomization } });
    }
    setEditingCartItem(null);
    setEditingCustomization(null);
  };

  const handleSaveBusinessCardCustomization = () => {
    if (editingCartItem && editingBusinessCardCustomization) {
      const cartItemId = editingCartItem.id || `${editingCartItem.productId}-${editingCartItem.variationId}`;
      dispatch({ type: 'UPDATE_BUSINESS_CARD_CUSTOMIZATION', payload: { cartItemId, businessCardCustomization: editingBusinessCardCustomization } });
    }
    setEditingCartItem(null);
    setEditingBusinessCardCustomization(null);
  };

  const handleCancelEdit = () => {
    setEditingCartItem(null);
    setEditingCustomization(null);
    setEditingBusinessCardCustomization(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md"
            aria-label="Continue shopping"
          >
            <ArrowLeft className="w-5 h-5 mr-2" aria-hidden="true" />
            Continue Shopping
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Shopping Cart</h1>
        </div>

        {!isGuest && !authCheckoutEnabled && (
          <div className="flex flex-row p-6 bg-[#f0f7ff] border border-[#cfe4ff] rounded-lg font-sans mb-6">
            <div className="mr-4 text-xl pt-1">
              ✨
            </div>
            <div className="flex flex-col gap-2">
              <h3 className="text-[#1a4a8e] text-lg font-semibold leading-tight">
                Checkout is coming soon!
              </h3>

              <p className="text-[#4a6fa5] text-[15px] leading-relaxed mb-2">
                You're previewing our new checkout experience. To complete your order, please use our current checkout.
                Your cart items will carry over automatically.
              </p>

              <button onClick={() => window.location.href = '/cart'} className="bg-[#1d5ed2] hover:bg-[#164eb3] text-white font-semibold py-3 px-6 rounded-md text-[15px] w-fit transition-colors duration-200">
                Complete Your Order &rarr;
              </button>
            </div>
          </div>
        )}

        {cart.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg shadow-sm" role="status">
            <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center" aria-hidden="true">
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
            <p className="text-gray-500 text-xl mb-2">Your cart is empty</p>
            <p className="text-gray-400 mb-6">Add some items to get started</p>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Browse Products
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-sm" role="region" aria-live="polite">
                <div className="p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Items ({cart.length})</h2>
                  <div className="space-y-6" role="list" aria-label="Cart items">
                    {Object.entries(
                      cart.reduce<Record<string, CartItem[]>>((groups, item) => {
                        const key = item.product.productionTimeline ?? 'Ships Soon';
                        (groups[key] ??= []).push(item);
                        return groups;
                      }, {})
                    ).map(([timelineName, items]) => (
                      <div key={timelineName}>
                        <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                          </svg>
                          <span className="text-sm font-semibold text-gray-600">Shipping Timeline:</span>
                          <span className="text-sm text-gray-900">{timelineName}</span>
                        </div>
                        <div className="space-y-6">
                        {items.map((item) => {
                          const cartItemId = item.id || `${item.productId}-${item.variationId}`;
                          const firstImage = item.variation.images && item.variation.images.length > 0
                            ? item.variation.images[0]
                            : item.product.images[0];
                          const variationImage = typeof firstImage === 'string' ? firstImage : firstImage.url;
                          const tier = getDiscountTier(item);
                          const discountedPrice = getDiscountedUnitPrice(item);
                          const personalizationFeePerUnit = getPersonalizationFeePerUnit(item);
                          const unitPrice = discountedPrice + personalizationFeePerUnit;
                          const lineTotal = unitPrice * item.quantity;

                          return (
                            <div key={cartItemId} className="flex space-x-4 pb-6 border-b border-gray-200 last:border-b-0 last:pb-0" role="listitem">
                              {/* Product Image */}
                              <div className="flex-shrink-0">
                                <img
                                  src={variationImage}
                                  alt={`${item.product.name} - ${item.variation.color}${item.variation.size !== 'Standard' ? `, ${item.variation.size}` : ''}`}
                                  className="w-24 h-24 rounded-lg object-cover"
                                />
                              </div>

                              {/* Product Details */}
                              <div className="flex-1 min-w-0">
                                <h3 className="text-base font-medium text-gray-900 mb-1">
                                  {item.product.name}
                                </h3>
                                <div className="text-sm text-gray-500 mb-2">
                                  {item.variation.size !== 'Standard' && (
                                    <span>Size: {item.variation.size} • </span>
                                  )}
                                  Color: {item.variation.color}
                                  
                                  {item.logoOptionName && <span className="block text-xs">Logo: {item.logoOptionName}</span>}
                                </div>

                                {/* Calendar Customization */}
                                {item.calendarCustomization && (
                                  <div className="mt-2 p-3 bg-blue-50 rounded-md border border-blue-100">
                                    <div className="flex items-start justify-between mb-1">
                                      <p className="text-xs font-medium text-blue-900">Calendar Customization</p>
                                      <button
                                        onClick={() => handleEditCustomization(item)}
                                        className="text-blue-600 hover:text-blue-800 transition-colors"
                                        aria-label="Edit calendar customization"
                                      >
                                        <Edit2 className="w-3 h-3" />
                                      </button>
                                    </div>
                                    <div className="text-xs text-blue-700 space-y-0.5">
                                      {item.calendarCustomization.locations.primary.street && (
                                        <p>{item.calendarCustomization.locations.primary.street}, {item.calendarCustomization.locations.primary.city}</p>
                                      )}
                                      {item.calendarCustomization.locations.secondary?.street && (
                                        <p>{item.calendarCustomization.locations.secondary.street}, {item.calendarCustomization.locations.secondary.city}</p>
                                      )}
                                      {item.calendarCustomization.locations.tertiary?.street && (
                                        <p>{item.calendarCustomization.locations.tertiary.street}, {item.calendarCustomization.locations.tertiary.city}</p>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Business Card Customization */}
                                {item.businessCardCustomization && (
                                  <div className="mt-2 p-3 bg-green-50 rounded-md border border-green-100">
                                    <div className="flex items-start justify-between">
                                      <p className="text-xs font-medium text-green-900">Business Card Customization</p>
                                      <button
                                        onClick={() => handleEditBusinessCardCustomization(item)}
                                        className="text-green-600 hover:text-green-800 transition-colors"
                                        aria-label="Edit business card customization"
                                      >
                                        <Edit2 className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </div>
                                )}

                                {/* Personalizations */}
                                {item.personalizations?.map((p, i) => (
                                  <div key={i} className="mt-2 p-3 bg-blue-100 rounded-md border border-blue-200">
                                    <p className="text-xs font-medium text-blue-900">
                                      {item.personalizations!.length > 1 ? `Personalization #${i + 1}` : 'Personalization'}
                                    </p>
                                    <p className="text-xs text-blue-700">{p.text}</p>
                                    {p.placement && <p className="text-xs text-blue-600">Placement: {p.placement}</p>}
                                    {p.fee != null && p.fee > 0 && <p className="text-xs text-blue-600">+${p.fee.toFixed(2)} per unit</p>}
                                  </div>
                                ))}

                                {/* Price and Quantity */}
                                <div className="mt-3 flex items-center justify-between">
                                  <div className="flex items-center gap-4">
                                    <div className="flex flex-col">
                                      <span className="text-xs text-gray-500">Unit Price</span>
                                       {tier ? (
                                        <div className="flex items-center gap-1">
                                          <span className="text-xs text-gray-400 line-through">${(item.variation.price + personalizationFeePerUnit).toFixed(2)}</span>
                                          <span className="text-sm font-medium text-gray-900">${unitPrice.toFixed(2)}</span>
                                          <span className="text-xs bg-green-100 text-green-700 rounded px-1">Qty Price</span>
                                        </div>
                                      ) : (
                                        <span className="text-sm font-medium text-gray-900">
                                          ${unitPrice.toFixed(2)}
                                        </span>
                                      )}
                                    </div>
                                    <QuantitySelector
                                      quantity={item.quantity}
                                      onQuantityChange={(newQuantity) => handleUpdateQuantity(cartItemId, newQuantity)}
                                      size="sm"
                                      stock={item.variation.stock}
                                      allowOnDemand={item.product.allowOnDemand}
                                    />
                                  </div>
                                  <div className="flex items-center gap-4">
                                    <div className="flex flex-col items-end">
                                      <span className="text-xs text-gray-500">Subtotal</span>
                                      {tier && (
                                        <span className="text-xs text-gray-400 line-through">
                                          ${((item.variation.price + personalizationFeePerUnit) * item.quantity).toFixed(2)}
                                        </span>
                                      )}
                                      <span className="text-base font-semibold text-gray-900">
                                        ${lineTotal.toFixed(2)}
                                      </span>
                                    </div>
                                    <button
                                      onClick={() => handleRemoveItem(cartItemId)}
                                      className="p-2 hover:bg-red-100 hover:text-red-600 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                                      aria-label={`Remove ${item.product.name} from cart`}
                                    >
                                      <Trash2 className="w-4 h-4" aria-hidden="true" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24" role="region" aria-label="Order summary">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h2>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal ({cart.length} {cart.length === 1 ? 'item' : 'items'})</span>
                    <span className="font-medium text-gray-900">${originalTotal.toFixed(2)}</span>
                  </div>
                  {totalSavings > 0.005 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Quantity Discount</span>
                      <span>-${totalSavings.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Shipping</span>
                    <span className="font-medium text-gray-900">Calculated at checkout</span>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">Estimated Total</span>
                    <span className="text-2xl font-bold text-gray-900">${totalPrice.toFixed(2)}</span>
                  </div>
                </div>

                <button
                  onClick={() => navigate('/checkout')}
                  disabled={!isGuest && !authCheckoutEnabled}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 mb-3"
                  aria-label={`Proceed to checkout with total $${totalPrice.toFixed(2)}`}
                >
                  Proceed to Checkout
                </button>

                <button
                  onClick={() => navigate('/')}
                  className="w-full bg-white hover:bg-gray-50 text-gray-700 font-medium py-3 px-4 rounded-lg border border-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Continue Shopping
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Calendar Customization Modal */}
      {editingCartItem && editingCustomization && (
        <Modal isOpen={true} onClose={handleCancelEdit} title="Edit Calendar Customization">
          <div className="max-h-[70vh] overflow-y-auto">
            <CalendarCustomizer
              onCustomizationChange={setEditingCustomization}
              initialCustomization={editingCustomization}
              variation={editingCartItem.variation}
              productImages={editingCartItem.product.images}
            />
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={handleCancelEdit} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button onClick={handleSaveCustomization} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors">
              Save Changes
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Business Card Customization Modal */}
      {editingCartItem && editingBusinessCardCustomization && (
        <Modal isOpen={true} onClose={handleCancelEdit} title="Edit Business Card Customization" size={"lg"}>
          <BusinessCardCustomizer
              onCustomizationChange={setEditingBusinessCardCustomization}
              initialCustomization={editingBusinessCardCustomization}
              withBorder={false}
              productImages={editingCartItem.product.images}
              previewImage={editingCartItem.product.printImages?.frontForReview}
              style={editingCartItem.product.printOnDemandStyle}
              fields={editingCartItem.product.printOnDemandFields}
              options={editingCartItem.product.printOnDemandOptions}
          />
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={handleCancelEdit} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button onClick={handleSaveBusinessCardCustomization} className="px-4 py-2 text-sm font-medium text-white bg-blue-300 rounded-md cursor-not-allowed" disabled>
              Save Changes
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
